import { useEffect, useMemo, useState } from 'react';
import { finApi } from '../../api/finance.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

export default function JournalEntries() {
  const { can } = useAuth();
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  const canCreate = can('journal_entry', 'create');
  const canPost = can('journal_entry', 'post');

  function refresh() {
    finApi.listJournalEntries().then((d) => setEntries(d?.items || []));
    finApi.accounts({ active: true }).then((d) => setAccounts(d || []));
  }
  useEffect(refresh, []);

  async function post(id) {
    try {
      await finApi.postJournalEntry(id);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  return (
    <section data-testid="journal-page">
      <header className="page-header">
        <h1>Journal Entries</h1>
        {canCreate && (
          <button
            className="btn primary"
            data-testid="create-journal-btn"
            onClick={() => setShowForm((s) => !s)}
          >
            {showForm ? 'Close' : '+ New Entry'}
          </button>
        )}
      </header>

      {error && <div className="alert error">{error}</div>}

      {showForm && (
        <NewEntryForm
          accounts={accounts}
          onCancel={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); refresh(); }}
        />
      )}

      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Entry No</th><th>Date</th><th>Description</th><th>Reference</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td><code>{e.entry_no}</code></td>
                <td>{e.date}</td>
                <td>{e.description}</td>
                <td className="muted">{e.reference || '—'}</td>
                <td><span className={`chip status-${e.status}`}>{e.status}</span></td>
                <td>
                  {e.status === 'draft' && canPost && (
                    <button
                      className="btn primary small"
                      data-testid="post-btn"
                      data-entry-id={e.id}
                      onClick={() => post(e.id)}
                    >
                      Post
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {entries.length === 0 && <tr><td colSpan={6} className="muted">No entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function NewEntryForm({ accounts, onSaved, onCancel }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState([
    { accountId: '', debit: '', credit: '', notes: '' },
    { accountId: '', debit: '', credit: '', notes: '' },
  ]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const totals = useMemo(() => {
    let debit = 0, credit = 0;
    for (const l of lines) {
      debit += Number(l.debit || 0);
      credit += Number(l.credit || 0);
    }
    return { debit: round(debit), credit: round(credit), diff: round(debit - credit) };
  }, [lines]);

  function setLine(i, k, v) {
    setLines((ls) => ls.map((l, j) => (i === j ? { ...l, [k]: v } : l)));
  }
  function addLine() { setLines((ls) => [...ls, { accountId: '', debit: '', credit: '', notes: '' }]); }
  function removeLine(i) { setLines((ls) => ls.filter((_, j) => j !== i)); }

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        date,
        description,
        reference: reference || null,
        lines: lines
          .filter((l) => l.accountId && (Number(l.debit) > 0 || Number(l.credit) > 0))
          .map((l) => ({
            accountId: l.accountId,
            debit: Number(l.debit || 0),
            credit: Number(l.credit || 0),
            notes: l.notes || null,
          })),
      };
      await finApi.createJournalEntry(payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  }

  const balanced = Math.abs(totals.diff) < 0.005;
  return (
    <form onSubmit={submit} className="card form-card" data-testid="journal-entry-form">
      <div className="grid-form">
        <label>Date<input data-testid="je-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required /></label>
        <label>Reference<input data-testid="je-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="optional" /></label>
        <label className="full">Description<input data-testid="je-description" value={description} onChange={(e) => setDescription(e.target.value)} required /></label>
      </div>

      <table className="data-table je-grid">
        <thead>
          <tr><th>Account</th><th>Debit</th><th>Credit</th><th>Notes</th><th></th></tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} data-testid={`je-line-${i}`}>
              <td>
                <select data-testid={`account-picker-${i}`} value={l.accountId} onChange={(e) => setLine(i, 'accountId', e.target.value)}>
                  <option value="">Select…</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                </select>
              </td>
              <td><input data-testid={`debit-${i}`} type="number" step="0.01" value={l.debit} onChange={(e) => setLine(i, 'debit', e.target.value)} /></td>
              <td><input data-testid={`credit-${i}`} type="number" step="0.01" value={l.credit} onChange={(e) => setLine(i, 'credit', e.target.value)} /></td>
              <td><input value={l.notes} onChange={(e) => setLine(i, 'notes', e.target.value)} /></td>
              <td>{lines.length > 2 && <button type="button" className="btn ghost small" onClick={() => removeLine(i)} aria-label={`Remove line ${i + 1}`}>×</button>}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td><button type="button" className="btn ghost small" data-testid="add-line-btn" onClick={addLine}>+ Add line</button></td>
            <td data-testid="debit-total"><strong>{totals.debit.toFixed(2)}</strong></td>
            <td data-testid="credit-total"><strong>{totals.credit.toFixed(2)}</strong></td>
            <td colSpan={2}
                data-testid="balance-indicator"
                data-balanced={balanced ? 'true' : 'false'}
                className={balanced ? 'balanced' : 'unbalanced'}>
              {balanced ? 'Balanced ✓' : `Out by ${totals.diff.toFixed(2)}`}
            </td>
          </tr>
        </tfoot>
      </table>

      {error && <div className="alert error" role="alert" data-testid="je-error">{error}</div>}
      <div className="actions">
        <button type="button" className="btn ghost" onClick={onCancel}>Cancel</button>
        <button
          type="submit"
          className="btn primary"
          data-testid="save-btn"
          disabled={submitting || !balanced}
        >
          {submitting ? 'Saving…' : 'Save Draft'}
        </button>
      </div>
    </form>
  );
}

function round(n) { return Math.round(Number(n) * 100) / 100; }
